import { useSyncExternalStore } from "react";

/**
 * How the contracts list takes a contract in or out of the run — a
 * prototype-only dial, not a product setting. The two variants are being
 * compared side by side:
 *
 *  - `checkboxes` — the current build: a contract stays on its tab and is
 *    ticked or unticked in place.
 *  - `move_buttons` — a contract is moved between Eligible and Skipped by an
 *    icon button, so which tab it is on always says what will happen to it.
 *  - `three_tabs` — the same moving, with what *can't* generate pulled out into
 *    a read-only Ineligible tab. Skipped then means only what the user chose to
 *    leave out, which is what makes a bulk action over it mean anything.
 *
 * Switching has to be instant and has to start the list over; the dev bar owns
 * the control, the card only reads it.
 */
export type SelectionBehavior = "checkboxes" | "move_buttons" | "three_tabs";

export const SELECTION_BEHAVIORS: { id: SelectionBehavior; label: string }[] = [
  { id: "checkboxes", label: "Checkboxes" },
  { id: "move_buttons", label: "2 tabs" },
  { id: "three_tabs", label: "3 tabs" },
];

const KEY = "brs-selection-behavior";
/** The current build, so the comparison starts from what exists today. */
const DEFAULT: SelectionBehavior = "checkboxes";

function read(): SelectionBehavior {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === "checkboxes" || stored === "move_buttons" || stored === "three_tabs") {
      return stored;
    }
  } catch {
    /* fall through to the default */
  }
  return DEFAULT;
}

let current: SelectionBehavior = read();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setSelectionBehavior(next: SelectionBehavior): void {
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    /* the choice just doesn't survive a reload */
  }
  for (const listener of listeners) listener();
}

export function useSelectionBehavior(): SelectionBehavior {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => DEFAULT,
  );
}
